import { ComponentPublicInstance, Ref, onMounted, onUnmounted, ref } from 'vue';
import { debounce } from 'lodash';

type AnchorRef = Ref<HTMLElement | ComponentPublicInstance | null | undefined>;

export interface TableRowsPerPageOptions {
    rowHeight: number;
    /**
     * Px used by everything that is not a row but still belongs to the table area: table head,
     * pagination, vertical margins, page bottom padding…
     * When no `anchor` is given it must also cover everything rendered above the table.
     */
    reservedHeight?: number;
    /**
     * Element where the table starts. When given, the space above it is measured instead of
     * assumed, so the view does not need a magic number for its own header, filters, etc.
     */
    anchor?: AnchorRef;
    minRows?: number;
    maxRows?: number;
    /** Used while the anchor cannot be measured: not mounted yet, or hidden (inactive tab, `v-show`). */
    fallbackRows?: number;
    /** Called when a viewport resize changes the result. Not called by `recalculate()`. */
    onChange?: (rowsPerPage: number) => void;
    debounceMs?: number;
}

const DEFAULT_MIN_ROWS = 5;
const RESIZE_DEBOUNCE_MS = 150;
const MEASURE_ATTEMPTS = 10;
const FRAME_TIMEOUT_MS = 50;

const nextFrame = () =>
    new Promise<void>((resolve) => {
        const timer = window.setTimeout(resolve, FRAME_TIMEOUT_MS);
        window.requestAnimationFrame(() => {
            window.clearTimeout(timer);
            resolve();
        });
    });

const resolveElement = (anchor?: AnchorRef): HTMLElement | undefined => {
    const value = anchor?.value;
    if (!value) return;

    return ('$el' in value ? value.$el : value) as HTMLElement;
};

/**
 * Page size derived from the viewport height, so a paginated table fills the screen without
 * scrolling it. Recomputed (debounced) on window resize.
 *
 * @example
 * const listRef = ref<HTMLElement>();
 * const { rowsPerPage, whenMeasured } = useTableRowsPerPage({
 *     rowHeight: 75,
 *     reservedHeight: 150,
 *     anchor: listRef,
 *     onChange: () => getItems(1)
 * });
 */
export const useTableRowsPerPage = ({
    rowHeight,
    reservedHeight = 0,
    anchor,
    minRows = DEFAULT_MIN_ROWS,
    maxRows = Number.POSITIVE_INFINITY,
    fallbackRows = DEFAULT_MIN_ROWS,
    onChange,
    debounceMs = RESIZE_DEBOUNCE_MS
}: TableRowsPerPageOptions) => {
    /** `undefined` when an anchor was given but cannot be measured yet. */
    const getSpaceAbove = (): number | undefined => {
        if (!anchor) return 0;

        const element = resolveElement(anchor);
        if (!element) return undefined;

        const rect = element.getBoundingClientRect();
        // A hidden element (inactive tab, `v-show`) reports an empty rect, which would otherwise
        // be read as "the table starts at the top of the viewport"
        if (!rect.top && !rect.height) return undefined;

        // `scrollY` keeps the measure independent of the current scroll position, otherwise
        // scrolling down would report more available space and grow the page even further.
        return rect.top + window.scrollY;
    };

    const calculate = (): number | undefined => {
        const spaceAbove = getSpaceAbove();
        if (spaceAbove === undefined) return undefined;

        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const rows = Math.floor((viewportHeight - spaceAbove - reservedHeight) / rowHeight);

        return Math.min(Math.max(rows, minRows), maxRows);
    };

    // Seeded synchronously so the first fetch already uses a sensible page size. With an `anchor`
    // the real value lands on mount, before the view fetches.
    const rowsPerPage = ref(calculate() ?? fallbackRows);

    const recalculate = (): boolean => {
        const rows = calculate();
        if (rows === undefined || rows === rowsPerPage.value) return false;

        rowsPerPage.value = rows;
        return true;
    };

    const onResize = debounce(() => {
        if (recalculate()) onChange?.(rowsPerPage.value);
    }, debounceMs);

    const measure = async (): Promise<number> => {
        for (let attempt = 0; attempt < MEASURE_ATTEMPTS && getSpaceAbove() === undefined; attempt++) {
            await nextFrame();
        }
        recalculate();

        return rowsPerPage.value;
    };

    let initialMeasure: Promise<number> | undefined;

    /**
     * Resolves with the first real measurement. The Angular bridge creates the Vue instance before
     * its template is inserted in the document, so at `mounted` the anchor is still detached and
     * every rect is 0 — it only becomes measurable a frame later. Await this before the first fetch.
     * Falls back to `fallbackRows` if the anchor never becomes measurable (hidden tab).
     */
    const whenMeasured = () => (initialMeasure ??= measure());

    onMounted(() => {
        whenMeasured();
        window.addEventListener('resize', onResize);
    });

    onUnmounted(() => {
        onResize.cancel();
        window.removeEventListener('resize', onResize);
    });

    return { rowsPerPage, recalculate, whenMeasured };
};
